import { experianInvoiceEmailLookup } from 'global/experianEmailValidation/experianInvoiceEmailLookup';
import ModalManager from 'global/modal/ModalManager';
import { getNodeFromString } from 'global/common/globalUtils';

/**
 * Creates a promise that resolves based on the user's modal interaction.
 * @param {HTMLElement} html - The modal's root HTML element.
 * @returns {Promise<boolean>} Resolves `true` if the user confirms, `false` if they cancel.
 */
const awaitModalDecision = html => {
	return new Promise(resolve => {
		let isProceeding = false;
		const modalSubmitBtn = html.querySelector('#submitAboutYouForm');

		if (modalSubmitBtn) {
			modalSubmitBtn.setAttribute('data-dismiss', 'modal');
			modalSubmitBtn.addEventListener(
				'click',
				() => {
					isProceeding = true;
					resolve(true);
				},
				{ once: true },
			);
		}

		jQuery(html).on('hidden.bs.modal', () => {
			if (!isProceeding) {
				resolve(false);
			}
			jQuery(html).remove();
		});
	});
};

/**
 * Displays the Experian modal with the provided response content.
 * @param {object} response - The API response object containing modal HTML in `response.data`.
 * @returns {Promise<boolean>} The promise from `awaitModalDecision`.
 */
const showExperianModal = response => {
	const html = getNodeFromString(response.data);
	const modal = new ModalManager();

	modal.insertResponse(html, html.id);
	return awaitModalDecision(html);
};

/**
 * Gets the analytics page type based on the current navigation context.
 * @returns {string} The mapped page type (e.g., 'registration', 'checkout') or an empty string.
 */
const getPageTypeForAnalytics = () => {
	const navigationPageType = window?.od?.journeyData?.navigation?.pageType;
	const pageTypeMap = {
		'register-multi': 'registration',
		'checkout-multi': 'checkout',
	};
	return pageTypeMap[navigationPageType] || '';
};

/**
 * Handles the Experian email validation check.
 * This is a "pausing" function called by the BillingFormController.
 * @returns {Promise<boolean>} Resolves `true` to proceed with submission, `false` to stop.
 */
const handleExperianEmailCheck = async () => {
	const emailInput = document.getElementById('invoiceEmailAddress');

	if (!emailInput || !emailInput.value) {
		return true;
	}

	try {
		const pageType = getPageTypeForAnalytics();
		const response = await experianInvoiceEmailLookup(
			emailInput.value,
			pageType,
		);

		if (response && response.status === 200 && response.data) {
			// Awaits the modal decision
			return await showExperianModal(response);
		}

		// Proceed if API fails or returns no data
		return true;
	} catch (error) {
		// Proceed on any exception
		return true;
	}
};

/**
 * Initializes the email validation logic by attaching the check function to the window.
 */
const initBillingFormInvoiceEmailValidation = () => {
	const billingForm = document.getElementById('billingForm');
	if (billingForm) {
		window.performExperianEmailCheck = handleExperianEmailCheck;
	}
};

export default initBillingFormInvoiceEmailValidation;
